import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContactInfo = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    phone: '',
    email: '',
    address: '',
    hours: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchContactInfo();
  }, [navigate]);

  const fetchContactInfo = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/contact-info`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.phone) {
          setContactInfo(data);
        }
      }
    } catch (error) {
      toast.error('Failed to load contact info');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setContactInfo({ ...contactInfo, [field]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      await fetch(`${BACKEND_URL}/api/admin/contact-info`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contactInfo)
      });
      
      toast.success('Contact information updated successfully!');
    } catch (error) {
      toast.error('Failed to update contact information');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white p-6 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="icon" className="text-white border-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Contact Information</h1>
              <p className="text-cyan-100">Update phone, email, and business hours</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-white text-cyan-600 hover:bg-cyan-50">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="container mx-auto p-6">
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="text-2xl">Business Contact Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>Phone Number</Label>
              <Input
                value={contactInfo.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="07123 456789"
              />
              <p className="text-sm text-gray-500 mt-1">Format: 07XXX XXXXXX</p>
            </div>

            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                value={contactInfo.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="tom@windowcleaner.com"
              />
            </div>

            <div>
              <Label>Business Address</Label>
              <Textarea
                value={contactInfo.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="123 Main Street, Neath, SA11 1XX"
                rows={3}
              />
            </div>

            <div>
              <Label>Business Hours</Label>
              <Textarea
                value={contactInfo.hours}
                onChange={(e) => handleChange('hours', e.target.value)}
                placeholder="Monday - Friday: 8am - 6pm&#10;Saturday: 9am - 4pm&#10;Sunday: Closed"
                rows={4}
              />
              <p className="text-sm text-gray-500 mt-1">Use line breaks to separate different days</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This information will be displayed on the Contact page and in the website footer.
                Make sure all details are accurate and up-to-date.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminContactInfo;
